/**
 * Instance Admin → Marketplace → "Get it built — orders & payouts".
 *
 * Twasta holds the client's money for every Get-it-built order and pays the
 * builder stage by stage (plan D5–D8). This screen is where the instance admin
 * and Twasta finance:
 *   - release approved stages (every check must pass) or hold / resolve them,
 *   - verify a builder's bank account and record the manual bank payout of
 *     their earnings with its bank reference (UTR) — plan D7,
 *   - read the TDS register,
 *   - look at any order.
 * Amounts are minor units; every rule is the server's (/build/admin/*).
 * Doc: docs/marketplace_get_it_built.md
 */
import { Fragment, useCallback, useEffect, useState, type CSSProperties, type FormEvent } from 'react'
import toast from 'react-hot-toast'
import { fmtDate, money, mktError } from '@/api/marketplace'
import {
  adminBuilderPayouts, adminHold, adminOrder, adminOrders, adminPayouts, adminRecordPayout, adminRelease, adminReleases,
  adminResolve, adminVerifyBank, ORDER_STATUS_LABELS, STAGE_STATUS_LABELS,
  type LedgerRow, type Order, type OrderStatus, type OrderSummary, type PayoutBoardRow, type PayoutRow, type ReleaseItem, type TdsRow,
} from '@/api/marketplaceBuild'
import { InfoBox, Section, Td, Th, chipStyle, tableStyle } from './adminUi'

type Sub = 'releases' | 'payouts' | 'tds' | 'orders'
const SUBS: [Sub, string][] = [['releases', 'Release queue'], ['payouts', 'Payouts'], ['tds', 'TDS register'], ['orders', 'All orders']]

const msg = (e: unknown, fallback: string) => mktError(e, fallback).message
const errStyle: CSSProperties = { color: 'var(--color-error)', fontSize: '0.875rem' }
const okChip: CSSProperties = { ...chipStyle, color: 'var(--color-success)', borderColor: 'var(--color-success)' }
const badChip: CSSProperties = { ...chipStyle, color: 'var(--color-error)', borderColor: 'var(--color-error)' }
const warnChip: CSSProperties = { ...chipStyle, color: 'var(--color-warning)', borderColor: 'var(--color-warning)' }
const card: CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 8, padding: '0.875rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', background: 'var(--color-bg-secondary)' }
const srOnly: CSSProperties = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }
const row: CSSProperties = { display: 'flex', gap: '0.5rem 1rem', flexWrap: 'wrap', alignItems: 'center' }

function toMinor(s: string): number { return Math.round((Number(String(s).replace(/[^\d.]/g, '')) || 0) * 100) }

export default function BuildOrdersPanel() {
  const [sub, setSub] = useState<Sub>('releases')
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <InfoBox>
        The client pays Twasta each stage's share; Twasta releases it to the builder's earnings once the client approves the stage and every check passes
        (the fee, tax on the fee and TDS are withheld). Twasta finance then pays the earnings balance to the builder's verified bank account and records the bank reference (UTR) here.
      </InfoBox>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="tablist" aria-label="Orders and payouts">
        {SUBS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={sub === id} className={`btn btn-sm ${sub === id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSub(id)}>{label}</button>
        ))}
      </div>
      {sub === 'releases' && <ReleaseQueue />}
      {sub === 'payouts' && <Payouts />}
      {sub === 'tds' && <TdsRegister />}
      {sub === 'orders' && <AllOrders />}
    </div>
  )
}

// ── release queue ───────────────────────────────────────────────────────────

function ReleaseQueue() {
  const [queue, setQueue] = useState<ReleaseItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(async () => {
    try {
      const r = await adminReleases()
      setQueue(r.queue)
      setError(null)
    } catch (e) {
      setError(msg(e, 'Could not load the release queue.'))
    }
  }, [])
  useEffect(() => { load() }, [load])

  return (
    <Section title="Release queue">
      <div style={{ ...row, justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Approved and disputed stages waiting for their money to move, oldest first.</span>
        <button type="button" className="btn btn-sm btn-secondary" onClick={load}>Refresh</button>
      </div>
      {error && <div role="alert" style={errStyle}>{error}</div>}
      {!queue && !error && <div role="status" style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>}
      {queue && queue.length === 0 && <InfoBox>Nothing is waiting. Approved stages that pass every check are released automatically.</InfoBox>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {queue?.map((q) => <ReleaseCard key={`${q.order_id}-${q.idx}`} q={q} onChanged={load} />)}
      </div>
    </Section>
  )
}

function ReleaseCard({ q, onChanged }: { q: ReleaseItem; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)
  const [mode, setMode] = useState<'' | 'hold' | 'resolve'>('')
  const [reason, setReason] = useState('')
  const [outcome, setOutcome] = useState<'approve' | 'refund' | 'changes'>('approve')
  const [error, setError] = useState<string | null>(null)
  const p = q.payout
  const cur = q.currency
  const disputed = q.status === 'disputed'

  const run = async (fn: () => Promise<unknown>, ok: string, fallback: string) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      toast.success(ok)
      setMode('')
      setReason('')
      onChanged()
    } catch (e) {
      const m = msg(e, fallback)
      setError(m)
      toast.error(m)
    } finally {
      setBusy(false)
    }
  }
  const submitHold = (e: FormEvent) => {
    e.preventDefault()
    if (!reason.trim()) { setError('Say why the stage is on hold — the builder and Twasta finance see it.'); return }
    run(() => adminHold(q.order_id, q.idx, true, reason.trim()), 'Stage put on hold', 'Could not put the stage on hold.')
  }
  const submitResolve = (e: FormEvent) => {
    e.preventDefault()
    if (!reason.trim()) { setError('Write a note explaining the decision — both sides see it.'); return }
    run(() => adminResolve(q.order_id, q.idx, outcome, reason.trim()), 'Dispute resolved', 'Could not resolve the dispute.')
  }

  return (
    <div style={card}>
      <div style={{ ...row, justifyContent: 'space-between' }}>
        <div>
          <strong>{q.order_no} · {q.title}</strong>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{q.stage} ({q.pct}%) · {q.builder} building for {q.client}</div>
        </div>
        <div style={row}>
          <span style={disputed ? badChip : q.ready ? okChip : chipStyle}>{STAGE_STATUS_LABELS[q.status] || q.status}</span>
          {q.on_hold && <span style={warnChip}>On hold</span>}
          {q.ready && !q.on_hold && !disputed && <span style={okChip}>Ready to release</span>}
        </div>
      </div>
      {q.approved_at && <div style={{ fontSize: '0.875rem' }}>Approved{q.approved_by ? ` by ${q.approved_by}` : ''} on {fmtDate(q.approved_at)}</div>}
      {q.on_hold && q.hold_reason && <div style={{ fontSize: '0.875rem' }}><strong>Hold reason:</strong> {q.hold_reason}</div>}
      {disputed && q.dispute_note && <div style={{ fontSize: '0.875rem' }}><strong>Dispute:</strong> {q.dispute_note}</div>}

      <div style={{ ...row, alignItems: 'flex-start', gap: '1.5rem' }}>
        <table style={{ ...tableStyle, marginTop: 0, width: 'auto', minWidth: 280 }} aria-label="Payout to the builder">
          <tbody>
            <tr><Td>Stage value (gross)</Td><Td right>{money(p.gross_minor, cur)}</Td></tr>
            <tr><Td>Twasta builder fee {p.fee_percent}%</Td><Td right>− {money(p.fee_minor, cur)}</Td></tr>
            {p.fee_tax_breakdown?.length
              ? p.fee_tax_breakdown.map((t) => <tr key={t.name}><Td>{t.name} {t.rate}% on the fee</Td><Td right>− {money(t.amount_minor, cur)}</Td></tr>)
              : p.fee_tax_minor ? <tr><Td>Tax on the fee</Td><Td right>− {money(p.fee_tax_minor, cur)}</Td></tr> : null}
            {p.tds_minor > 0 && <tr><Td>TDS {p.tds_percent}%</Td><Td right>− {money(p.tds_minor, cur)}</Td></tr>}
            <tr><Td><strong>To the builder</strong></Td><Td right><strong>{money(p.net_minor, cur)}</strong></Td></tr>
          </tbody>
        </table>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem' }}>
          <strong>Checks before release</strong>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {q.checks.map((c) => (
              <li key={c.key} style={{ color: c.ok ? 'var(--color-text)' : 'var(--color-error)' }}>
                <span aria-hidden="true" style={{ color: c.ok ? 'var(--color-success)' : 'var(--color-error)', fontWeight: 700, marginRight: 6 }}>{c.ok ? '✓' : '✕'}</span>
                {c.label}<span style={srOnly}>{c.ok ? ' — passed' : ' — not passed'}</span>
              </li>
            ))}
          </ul>
          <span style={{ marginTop: 4 }}>
            Bank: {q.bank.last4 ? `••••${q.bank.last4}` : 'not added'}{' '}
            <span style={q.bank.verified ? okChip : warnChip}>{q.bank.verified ? 'Verified' : 'Not verified'}</span>
          </span>
        </div>
      </div>

      {error && <div role="alert" style={errStyle}>{error}</div>}

      <div style={row}>
        {!disputed && (
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || !q.ready || q.on_hold}
            title={!q.ready ? 'Every check must pass first' : q.on_hold ? 'Remove the hold first' : undefined}
            onClick={() => run(() => adminRelease(q.order_id, q.idx), `Released ${money(p.net_minor, cur)} to ${q.builder}`, 'Could not release the stage.')}>
            Release {money(p.net_minor, cur)} now
          </button>
        )}
        {q.on_hold
          ? <button type="button" className="btn btn-sm btn-secondary" disabled={busy} onClick={() => run(() => adminHold(q.order_id, q.idx, false), 'Hold removed', 'Could not remove the hold.')}>Remove hold</button>
          : <button type="button" className="btn btn-sm btn-secondary" disabled={busy} aria-expanded={mode === 'hold'} onClick={() => { setMode(mode === 'hold' ? '' : 'hold'); setError(null) }}>Hold</button>}
        {disputed && <button type="button" className="btn btn-sm btn-primary" disabled={busy} aria-expanded={mode === 'resolve'} onClick={() => { setMode(mode === 'resolve' ? '' : 'resolve'); setError(null) }}>Resolve the dispute</button>}
      </div>

      {mode === 'hold' && (
        <form onSubmit={submitHold} style={{ ...row, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem', flex: '1 1 320px' }}>
            Why hold it?
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={255} placeholder="e.g. checking a bank account change" />
          </label>
          <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>Put on hold</button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setMode('')}>Cancel</button>
        </form>
      )}
      {mode === 'resolve' && (
        <form onSubmit={submitResolve} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <fieldset style={{ border: 0, padding: 0, margin: 0, ...row }}>
            <legend style={{ fontSize: '0.875rem', marginBottom: 4 }}>Decision</legend>
            {([['approve', 'Approve — pay the builder'], ['refund', 'Refund the client'], ['changes', 'Builder makes changes']] as const).map(([id, label]) => (
              <label key={id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.875rem' }}>
                <input type="radio" name={`outcome-${q.order_id}-${q.idx}`} checked={outcome === id} onChange={() => setOutcome(id)} />{label}
              </label>
            ))}
          </fieldset>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem' }}>
            Note to both sides
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
          </label>
          <div style={row}>
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>Save decision</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setMode('')}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  )
}

// ── payouts ─────────────────────────────────────────────────────────────────

function Payouts() {
  const [rows, setRows] = useState<PayoutBoardRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string>('')        // `${tenant}:${currency}` with the payout form open
  const [history, setHistory] = useState<string>('')  // tenant whose history is open
  const load = useCallback(async () => {
    try {
      const r = await adminPayouts()
      setRows(r.builders)
      setError(null)
    } catch (e) {
      setError(msg(e, 'Could not load builder payouts.'))
    }
  }, [])
  useEffect(() => { load() }, [load])

  const verify = async (r: PayoutBoardRow, on: boolean) => {
    try {
      await adminVerifyBank(r.tenant_id, on)
      toast.success(on ? `${r.builder}'s bank account is verified` : 'Verification removed')
      load()
    } catch (e) {
      toast.error(msg(e, 'Could not change the verification.'))
    }
  }

  return (
    <Section title="Payouts to builders">
      <div style={{ ...row, justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Pay the earnings balance to the builder's bank, then record it here with the bank reference (UTR). A changed bank account must be verified again before a payout.</span>
        <button type="button" className="btn btn-sm btn-secondary" onClick={load}>Refresh</button>
      </div>
      {error && <div role="alert" style={errStyle}>{error}</div>}
      {!rows && !error && <div role="status" style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>}
      {rows && rows.length === 0 && <InfoBox>No builder has earnings yet.</InfoBox>}
      {rows && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr><Th>Builder</Th><Th right>Balance</Th><Th>Bank account</Th><Th>Last payout</Th><Th>{null}</Th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const key = `${r.tenant_id}:${r.currency}`
                const b = r.bank
                return (
                  <Fragment key={key}>
                    <tr>
                      <Td><strong>{r.builder}</strong></Td>
                      <Td right><strong>{money(r.balance_minor, r.currency)}</strong></Td>
                      <Td>
                        {b?.last4 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span>{b.holder || '—'} · {b.name || 'Bank'} ••••{b.last4}</span>
                            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>IFSC {b.ifsc || '—'} · PAN {b.pan || '—'}{b.gstin ? ` · GSTIN ${b.gstin}` : ''}{b.changed_at ? ` · changed ${fmtDate(b.changed_at)}` : ''}</span>
                            <span><span style={b.verified ? okChip : warnChip}>{b.verified ? 'Verified' : 'Not verified'}</span></span>
                          </div>
                        ) : <span style={{ color: 'var(--color-text-secondary)' }}>Not added</span>}
                      </Td>
                      <Td>{r.last_payout ? <span>{money(r.last_payout.amount_minor, r.last_payout.currency)} on {fmtDate(r.last_payout.paid_at)}<br /><span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>UTR {r.last_payout.utr}</span></span> : '—'}</Td>
                      <Td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {b?.last4 && <button type="button" className="btn btn-sm btn-secondary" onClick={() => verify(r, !b.verified)}>{b.verified ? 'Remove verification' : 'Verify bank'}</button>}
                          <button type="button" className="btn btn-sm btn-primary" disabled={!b?.verified || r.balance_minor <= 0} aria-expanded={open === key}
                            title={!b?.verified ? 'Verify the bank account first' : r.balance_minor <= 0 ? 'Nothing to pay' : undefined}
                            onClick={() => setOpen(open === key ? '' : key)}>Record payout</button>
                          <button type="button" className="btn btn-sm btn-ghost" aria-expanded={history === r.tenant_id} onClick={() => setHistory(history === r.tenant_id ? '' : r.tenant_id)}>History</button>
                        </div>
                      </Td>
                    </tr>
                    {open === key && (
                      <tr><td colSpan={5} style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
                        <PayoutForm r={r} onDone={() => { setOpen(''); load() }} onCancel={() => setOpen('')} />
                      </td></tr>
                    )}
                    {history === r.tenant_id && (
                      <tr><td colSpan={5} style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
                        <BuilderHistory tenantId={r.tenant_id} />
                      </td></tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

function PayoutForm({ r, onDone, onCancel }: { r: PayoutBoardRow; onDone: () => void; onCancel: () => void }) {
  const [amount, setAmount] = useState(String(r.balance_minor / 100))
  const [utr, setUtr] = useState('')
  const [method, setMethod] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const minor = toMinor(amount)
  const over = minor > r.balance_minor

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (minor <= 0 || over) { setError(`The amount must be above zero and at most ${money(r.balance_minor, r.currency)}.`); return }
    if (utr.trim().length < 6) { setError('Enter the bank reference (UTR) of the transfer.'); return }
    setBusy(true)
    setError(null)
    try {
      await adminRecordPayout({ tenant_id: r.tenant_id, currency: r.currency, amount_minor: minor, utr: utr.trim(), method: method.trim() || undefined, note: note.trim() || undefined })
      toast.success(`Payout of ${money(minor, r.currency)} recorded`)
      onDone()
    } catch (ex) {
      const m = msg(ex, 'Could not record the payout.')
      setError(m)
      toast.error(m)
    } finally {
      setBusy(false)
    }
  }

  const lbl: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem' }
  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }} aria-label={`Record a payout to ${r.builder}`}>
      <div style={{ ...row, alignItems: 'flex-end' }}>
        <label style={lbl}>Amount paid ({r.currency})
          <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} aria-invalid={over} style={{ width: 140 }} />
        </label>
        <label style={lbl}>Bank reference (UTR)
          <input value={utr} onChange={(e) => setUtr(e.target.value)} maxLength={64} style={{ width: 220 }} />
        </label>
        <label style={lbl}>How it was paid
          <input value={method} onChange={(e) => setMethod(e.target.value)} maxLength={16} list="payout-methods" style={{ width: 120 }} />
          <datalist id="payout-methods"><option value="NEFT" /><option value="RTGS" /><option value="IMPS" /><option value="UPI" /></datalist>
        </label>
        <label style={{ ...lbl, flex: '1 1 200px' }}>Note
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} />
        </label>
      </div>
      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Balance {money(r.balance_minor, r.currency)} · paid to ••••{r.bank.last4}. The builder is emailed the amount and the UTR.</span>
      {error && <div role="alert" style={errStyle}>{error}</div>}
      <div style={row}>
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>{busy ? 'Recording…' : 'Record payout'}</button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}

function BuilderHistory({ tenantId }: { tenantId: string }) {
  const [data, setData] = useState<{ payouts: PayoutRow[]; ledger: LedgerRow[] } | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    adminBuilderPayouts(tenantId).then((d) => { if (live) setData(d) }).catch((e) => { if (live) setError(msg(e, 'Could not load the history.')) })
    return () => { live = false }
  }, [tenantId])
  if (error) return <div role="alert" style={errStyle}>{error}</div>
  if (!data) return <div role="status" style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div>
        <strong style={{ fontSize: '0.875rem' }}>Payouts</strong>
        {data.payouts.length === 0 ? <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>None yet.</div> : (
          <table style={{ ...tableStyle, marginTop: 4 }}>
            <thead><tr><Th>Date</Th><Th right>Amount</Th><Th>UTR</Th><Th>How</Th><Th>Note</Th></tr></thead>
            <tbody>{data.payouts.map((p) => <tr key={p.id}><Td>{fmtDate(p.paid_at)}</Td><Td right>{money(p.amount_minor, p.currency)}</Td><Td>{p.utr}</Td><Td>{p.method || '—'}</Td><Td>{p.note || '—'}</Td></tr>)}</tbody>
          </table>
        )}
      </div>
      <div>
        <strong style={{ fontSize: '0.875rem' }}>Released stages</strong>
        {data.ledger.length === 0 ? <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>None yet.</div> : (
          <table style={{ ...tableStyle, marginTop: 4 }}>
            <thead><tr><Th>Date</Th><Th>Order · stage</Th><Th right>Gross</Th><Th right>Fee</Th><Th right>Tax on fee</Th><Th right>TDS</Th><Th right>Net</Th></tr></thead>
            <tbody>{data.ledger.map((l, i) => (
              <tr key={`${l.order_id}-${l.stage}-${i}`}><Td>{l.paid_at ? fmtDate(l.paid_at) : '—'}</Td><Td>{l.order_no} · {l.stage}</Td><Td right>{money(l.gross_minor, l.currency)}</Td><Td right>{money(l.fee_minor, l.currency)}</Td><Td right>{money(l.fee_tax_minor, l.currency)}</Td><Td right>{money(l.tds_minor, l.currency)}</Td><Td right>{money(l.net_minor, l.currency)}</Td></tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ── TDS register ────────────────────────────────────────────────────────────

function TdsRegister() {
  const [rows, setRows] = useState<TdsRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    adminPayouts().then((r) => setRows(r.tds)).catch((e) => setError(msg(e, 'Could not load the TDS register.')))
  }, [])
  return (
    <Section title="TDS register">
      <InfoBox>TDS withheld from builder payouts, per builder and quarter — what Twasta deposits and reports for each PAN.</InfoBox>
      {error && <div role="alert" style={errStyle}>{error}</div>}
      {!rows && !error && <div role="status" style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>}
      {rows && rows.length === 0 && <div style={{ marginTop: '0.75rem', color: 'var(--color-text-secondary)' }}>No TDS withheld yet.</div>}
      {rows && rows.length > 0 && (
        <table style={tableStyle}>
          <thead><tr><Th>Quarter</Th><Th>Builder</Th><Th>PAN</Th><Th right>TDS withheld</Th></tr></thead>
          <tbody>{rows.map((t, i) => <tr key={`${t.tenant_id}-${t.quarter}-${t.currency}-${i}`}><Td>{t.quarter}</Td><Td>{t.builder}</Td><Td>{t.pan || <span style={badChip}>PAN missing</span>}</Td><Td right>{money(t.tds_minor, t.currency)}</Td></tr>)}</tbody>
        </table>
      )}
    </Section>
  )
}

// ── all orders ──────────────────────────────────────────────────────────────

function AllOrders() {
  const [status, setStatus] = useState<OrderStatus | ''>('')
  const [orders, setOrders] = useState<OrderSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<number | null>(null)
  const load = useCallback(async () => {
    try {
      const r = await adminOrders(status || undefined)
      setOrders(r.orders)
      setError(null)
    } catch (e) {
      setError(msg(e, 'Could not load the orders.'))
    }
  }, [status])
  useEffect(() => { load() }, [load])

  return (
    <Section title="All orders">
      <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: '0.875rem' }}>
        Status
        <select value={status} onChange={(e) => { setStatus(e.target.value as OrderStatus | ''); setSelected(null) }}>
          <option value="">Every status</option>
          {(Object.keys(ORDER_STATUS_LABELS) as OrderStatus[]).map((s) => <option key={s} value={s}>{ORDER_STATUS_LABELS[s]}</option>)}
        </select>
      </label>
      {error && <div role="alert" style={{ ...errStyle, marginTop: '0.5rem' }}>{error}</div>}
      {!orders && !error && <div role="status" style={{ color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>Loading…</div>}
      {orders && orders.length === 0 && <div style={{ marginTop: '0.75rem', color: 'var(--color-text-secondary)' }}>No orders{status ? ' with this status' : ' yet'}.</div>}
      {orders && orders.length > 0 && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '2 1 560px', minWidth: 0, overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr><Th>Order</Th><Th>Client → builder</Th><Th>Status</Th><Th>Current stage</Th><Th right>Price</Th><Th right>Held</Th><Th right>Paid to builder</Th></tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} style={{ background: selected === o.id ? 'var(--color-bg-secondary)' : undefined }}>
                    <Td>
                      <button type="button" onClick={() => setSelected(o.id)} aria-pressed={selected === o.id}
                        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--color-accent)', cursor: 'pointer', textAlign: 'left' }}>
                        <strong>{o.order_no}</strong><br />{o.title}
                      </button>
                    </Td>
                    <Td>{o.client} → {o.builder}</Td>
                    <Td><span style={chipStyle}>{ORDER_STATUS_LABELS[o.status] || o.status}</span></Td>
                    <Td>{o.current ? `${o.current.name} — ${STAGE_STATUS_LABELS[o.current.status] || o.current.status}` : '—'}</Td>
                    <Td right>{money(o.price_minor, o.currency)}</Td>
                    <Td right>{money(o.money.held_minor, o.currency)}</Td>
                    <Td right>{money(o.money.paid_to_builder_minor, o.currency)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selected != null && <OrderDetail id={selected} onClose={() => setSelected(null)} />}
        </div>
      )}
    </Section>
  )
}

function OrderDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const [o, setO] = useState<Order | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    setO(null)
    setError(null)
    adminOrder(id).then((x) => { if (live) setO(x) }).catch((e) => { if (live) setError(msg(e, 'Could not load the order.')) })
    return () => { live = false }
  }, [id])

  return (
    <aside style={{ ...card, flex: '1 1 380px', minWidth: 0 }} aria-label="Order details">
      <div style={{ ...row, justifyContent: 'space-between' }}>
        <strong>{o ? `${o.order_no} · ${o.title}` : 'Order'}</strong>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
      </div>
      {error && <div role="alert" style={errStyle}>{error}</div>}
      {!o && !error && <div role="status" style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>}
      {o && (
        <>
          <div style={{ fontSize: '0.875rem' }}>
            {o.client.name} ({o.client.person}) → {o.builder.name} · <span style={chipStyle}>{ORDER_STATUS_LABELS[o.status] || o.status}</span>
            <div style={{ color: 'var(--color-text-secondary)' }}>Placed {fmtDate(o.created_at)}{o.signed_name ? ` · signed by ${o.signed_name}` : ''}{o.ended_at ? ` · ended ${fmtDate(o.ended_at)}` : ''}{o.end_reason ? ` (${o.end_reason})` : ''}</div>
          </div>
          <table style={{ ...tableStyle, marginTop: 0 }} aria-label="Money">
            <tbody>
              <tr><Td>Price</Td><Td right>{money(o.money.price_minor, o.currency)}</Td></tr>
              <tr><Td>Collected from the client</Td><Td right>{money(o.money.collected_minor, o.currency)}</Td></tr>
              <tr><Td>Client service fees</Td><Td right>{money(o.money.service_fees_minor, o.currency)}</Td></tr>
              <tr><Td>Held by Twasta</Td><Td right>{money(o.money.held_minor, o.currency)}</Td></tr>
              <tr><Td>Released (gross)</Td><Td right>{money(o.money.released_gross_minor, o.currency)}</Td></tr>
              <tr><Td>Builder fees · tax on fees · TDS</Td><Td right>{money(o.money.builder_fees_minor, o.currency)} · {money(o.money.fee_tax_minor, o.currency)} · {money(o.money.tds_minor, o.currency)}</Td></tr>
              <tr><Td>Paid to the builder</Td><Td right>{money(o.money.paid_to_builder_minor, o.currency)}</Td></tr>
              <tr><Td>Refunded</Td><Td right>{money(o.money.refunded_minor, o.currency)}</Td></tr>
              <tr><Td>Still to pay</Td><Td right>{money(o.money.still_to_pay_minor, o.currency)}</Td></tr>
            </tbody>
          </table>
          <strong style={{ fontSize: '0.875rem' }}>Stages</strong>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...tableStyle, marginTop: 0 }}>
              <thead><tr><Th>Stage</Th><Th right>Amount</Th><Th>Status</Th><Th right>Net to builder</Th></tr></thead>
              <tbody>
                {o.stages.map((s) => (
                  <tr key={s.idx}>
                    <Td>{s.name} ({s.pct}%){s.on_hold ? <> <span style={warnChip}>On hold</span></> : null}{s.target_date ? <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Target {fmtDate(s.target_date)}</div> : null}</Td>
                    <Td right>{money(s.amount_minor, o.currency)}</Td>
                    <Td>{STAGE_STATUS_LABELS[s.status] || s.status}{s.dispute_note ? <div style={{ fontSize: '0.8125rem' }}>Dispute: {s.dispute_note}</div> : null}</Td>
                    <Td right>{s.released_net_minor != null ? money(s.released_net_minor, o.currency) : s.payout ? `${money(s.payout.net_minor, o.currency)} when released` : '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <strong style={{ fontSize: '0.875rem' }}>Events</strong>
          {o.events.length === 0 ? <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>None.</span> : (
            <ol style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.875rem', display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 320, overflowY: 'auto' }}>
              {o.events.slice().sort((a, b) => b.at.localeCompare(a.at)).map((ev) => (
                <li key={ev.id}><span style={{ color: 'var(--color-text-secondary)' }}>{fmtDate(ev.at)}{ev.actor ? ` · ${ev.actor}` : ''}:</span> {ev.text}</li>
              ))}
            </ol>
          )}
        </>
      )}
    </aside>
  )
}
